import { Button, Icon } from '@platform/components';
import dataTestids from '../../constants/test-ids';
import NavbarDropdown, { NavbarDropdownItem } from '../../dashboard/NavbarDropdown';
import { MovingWorldsBannerLogo } from '../../layout/MovingWorldsLogo';

const forIndividualItems: NavbarDropdownItem[] = [
  {
    title: 'TRANSFORM Support Hub for Professionals',
    href: '/transform/professionals',
    linkType: 'legacy',
  },
  {
    title: 'Career Growth Resources & Guides',
    href: `/resources-and-guides#career-change-for-social-impact`,
    linkType: 'legacy',
  },
];

const forSocialEnterprisesItems: NavbarDropdownItem[] = [
  {
    title: 'How can we support you',
    href: '/organization',
    linkType: 'legacy',
  },
  {
    title: 'TRANSFORM Support Hub for Social Enterprises',
    href: `/transform/social-enterprises`,
    linkType: 'legacy',
  },
  {
    title: 'Social Enterprise Resources & Guides',
    href: `/resources-and-guides`,
    linkType: 'legacy',
  },
];

const forCorporatesItems: NavbarDropdownItem[] = [
  {
    title: 'CSR Services',
    href: '/corporate-services',
    linkType: 'legacy',
  },
  {
    title: 'TRANSFORM Support Hub for Corporates',
    href: `/transform/corporate`,
    linkType: 'legacy',
  },
  {
    title: 'Social Responsibility Resources & Guides',
    href: `/resources-and-guides#corporate-social-responsibility-and-esg`,
    linkType: 'legacy',
  },
];

const aboutItems: NavbarDropdownItem[] = [
  {
    title: 'What we do and why',
    href: '/about',
    linkType: 'legacy',
    additionalClassnames: 'text-md',
  },
  {
    title: 'Meet the team',
    href: `/about#get-to-know-the-team`,
    linkType: 'legacy',
  },
  {
    title: 'Our Impact',
    href: `/impact-of-experteering`,
    linkType: 'legacy',
  },
  {
    title: 'Our Network',
    href: `/network-report`,
    linkType: 'legacy',
  },
  {
    title: 'Career',
    href: `/about#our-commitment-to-inclusive-hiring`,
    linkType: 'legacy',
  },
  {
    title: 'Blog',
    href: `https://blog.movingworlds.org`,
    linkType: 'external',
  },
];

export const UnauthedNavbar = ({
  isSidebarOpen,
  setIsSidebarOpen,
  sidebarRef,
}: {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (isOpen: boolean) => void;
  sidebarRef: React.RefObject<HTMLDivElement>;
}) => {
  const navBarSection = (
    <>
      <NavbarDropdown
        buttonText='For Individuals'
        items={forIndividualItems}
        isLoading={false}
        additionalTitleClassnames='text-xl md:text-sm'
        buttonPropsOverride={{
          size: 'md',
          variant: 'primary',
          additionalClassnames:
            'md:text-white md:bg-brand-500 md:hover:bg-brand-500 border-none focus:none',
          children: 'For Individuals',
        }}
        dataTestId='for-individuals'
      />
      <NavbarDropdown
        buttonText='For Social Enterprises'
        items={forSocialEnterprisesItems}
        isLoading={false}
        additionalTitleClassnames='text-xl md:text-sm'
        buttonPropsOverride={{
          size: 'md',
          variant: 'primary',
          additionalClassnames:
            'md:text-white md:bg-brand-500 md:hover:bg-brand-500 border-none focus:none',
          children: 'For Social Enterprises',
        }}
        dataTestId='for-social-enterprises'
      />
      <NavbarDropdown
        buttonText='For Corporates'
        items={forCorporatesItems}
        isLoading={false}
        additionalTitleClassnames='text-xl md:text-sm'
        buttonPropsOverride={{
          size: 'md',
          variant: 'primary',
          additionalClassnames:
            'md:text-white md:bg-brand-500 md:hover:bg-brand-500 border-none focus:none',
          children: 'For Corporates',
        }}
        dataTestId='for-corporates'
      />
      <NavbarDropdown
        buttonText='About'
        items={aboutItems}
        isLoading={false}
        additionalTitleClassnames='text-xl md:text-sm'
        buttonPropsOverride={{
          size: 'md',
          variant: 'primary',
          additionalClassnames:
            'md:text-white md:bg-brand-500 md:hover:bg-brand-500 border-none focus:none',
          children: 'About',
        }}
        dataTestId='about'
      />
    </>
  );

  const loginSection = (
    <>
      <div className='p-1 md:p-0 w-[83px] flex md:justify-center md:text-center'>
        <a className='text-xl md:text-lg font-medium w-full' href='/platform/auth/login'>
          Log In
        </a>
      </div>
      <Button
        variant='tertiary-color'
        size='xl'
        href='/platform/auth/sign-up'
        additionalClassnames='p-1 md:p-0 w-full md:w-[69px] text-gray-500 border-none bg-accent-500 active:none focus:none hover:bg-accent-500'
      >
        Join
      </Button>
    </>
  );

  return (
    <div
      className={`z-40 sticky top-0 md:relative md:top-auto bg-brand-500 h-[54px] md:h-[var(--navbar-height)] px-4 lg:px-10 py-6 flex items-center`}
    >
      <div className='text-white flex flex-row justify-between w-full'>
        {/* Lazy fix for bad SVG sizing; need to re-export the SVG */}
        <a
          className='flex items-center'
          href={import.meta.env.VITE_LEGACY_APP_URL}
          data-testid={dataTestids.LAYOUT.HEADER.LOGO}
        >
          {/* The legacy app uses a different size around roughly the 'md' breakpoint here, but this has been ignored in favor of us redefining how we want to handle mobile responsiveness in the future */}
          <div className='w-[200px] lg:w-[272px]'>
            <MovingWorldsBannerLogo />
          </div>
        </a>
        <div className='hidden md:flex flex-row gap-4 items-center ml-4'>
          {navBarSection}
          {loginSection}
        </div>

        {isSidebarOpen && (
          <div
            className='md:hidden fixed top-0 left-0 w-full h-screen bg-black bg-opacity-50 z-50 text-gray-500 text-md'
            data-testid={dataTestids.LAYOUT.HEADER.MOBILE_SIDEPANEL}
          >
            <div
              ref={sidebarRef}
              className='fixed top-0 left-0 overflow-y-auto w-[calc(100vw-64px)] h-screen bg-white z-50 p-2'
            >
              {navBarSection}
              {loginSection}
            </div>
          </div>
        )}
        <button
          className='flex md:hidden cursor-pointer mr-[-12px] p-4'
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          data-testid={dataTestids.LAYOUT.HEADER.MOBILE_SIDEPANEL_BUTTON}
        >
          <Icon type='menu-02' size='24' stroke='white' />
        </button>
      </div>
    </div>
  );
};
